import React, { useState } from 'react';
import { NextSeo } from 'next-seo';
import css from 'styled-jsx/css';
import MainLayout from 'components/layout/MainLayout';
import PlayFreeImageCarousel from 'components/play-free-lp/PlayFreeImageCarousel/PlayFreeImageCarousel';
import PlayFreeSignUpAndEnter from 'components/play-free-lp/PlayFreeSignUpAndEnter/PlayFreeSignUpAndEnter';
import AboutUsPopup from 'components/play-free-lp/AboutUsPopup/AboutUsPopup';
import BrandDetailsMobileFooter from 'components/play-free-lp/BrandDetailsMobileFooter/BrandDetailsMobileFooter';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import Link from 'next/link';
import { ASSET_PREFIX, colors } from 'utils/constants';
import s from 'components/play-free-lp/PlayFreeImageCarousel/PlayFreeImageCarousel.module.css';
import PlayFreeMoreInfoLink from 'components/play-free-lp/PlayFreeMoreInfoLink/PlayFreeMoreInfoLink';
import { useRouter } from 'next/router';
import { scrollToSection } from 'utils/common';

const Variant = {
  BARBADOS: '0',
  GENERAL: '1',
  LA_GOMERA: '2',
};

interface IPrizeCategoriesSectionProps { 
  onClick?: () => void
}

const PrizeCategoriesSection = ({ onClick }: IPrizeCategoriesSectionProps) => (
  <div className="prizeCategoriesSection">
    <div className="prizeCategory">
      <img
        src={`${ASSET_PREFIX}/images/play-free-lp/circle-img-trips-and-stays.png`}
        alt="Woman sat with her arms raised on a boat which is floating on a river lined with trees"
        className="prizeCategoryImg"
        onClick={onClick}
      />
      <div className="headline grey prizeCategoryDescription">
        EPIC TRIPS
        <br />& STAYS
      </div>
    </div>

    <div className="prizeCategory">
      <img
        src={`${ASSET_PREFIX}/images/play-free-lp/circle-img-experiences.png`}
        alt="Person taking a picture of the natural scenery"
        className="prizeCategoryImg"
        onClick={onClick}
      />
      <div className="headline grey prizeCategoryDescription">
        UNFORGETTABLE
        <br />
        EXPERIENCES
      </div>
    </div>

    <div className="prizeCategory">
      <img
        src={`${ASSET_PREFIX}/images/play-free-lp/circle-img-lifestyle.png`}
        alt="Woman laughing while wearing a Lush face mask"
        className="prizeCategoryImg"
        onClick={onClick}
      />
      <div className="headline grey prizeCategoryDescription">
        LIFESTYLE
        <br />& BEAUTY
      </div>
    </div>

    <div className="prizeCategory">
      <img
        src={`${ASSET_PREFIX}/images/play-free-lp/circle-img-gadgets.png`}
        alt="A games console in the style of a cartoon"
        className="prizeCategoryImg"
        onClick={onClick}
      />
      <div className="headline grey prizeCategoryDescription">
        AWESOME
        <br />
        GADGETS
      </div>
    </div>

    <style>
      {`
        .headline {
          text-align: center;
          color: white;
          font-size: 1.4em;
          font-weight: 800;
          text-shadow: 2px 2px #4A4A4A;
        }
      
        .headline.grey {
          color: #4A4A4A;
          text-shadow: 2px 2px white;
        }
        
        .desktopContainer .headline {
          text-align: left;
          font-size: 2.7em;
        }
        
        @media (min-width: 1400px) {
          .desktopContainer .headline {
            font-size: 3em;
          }
        }
        
        @media (min-width: 1700px) {
          .desktopContainer .headline {
            font-size: 3.5em;
          }
        }
        
        @media (min-width: 2000px) {
          .desktopContainer .headline {
            font-size: 4em;
          }
        }
      
        .prizeCategoriesSection {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-auto-rows: 1fr;
          grid-gap: 1em;
          row-gap: 2.5em;
          width: 100%;
          margin-top: 2em;
          max-width: 375px;
        }
        
        .prizeCategory {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .prizeCategoryImg {
          width: 85px;
          height: 80px;
        }
        
        .prizeCategoryDescription {
          text-align: center;
          font-size: 0.9em;
          margin-top: 0.8em;
        }
        
        @media (min-width: 350px) {
          .prizeCategoryImg {
            width: 95px;
            height: 90px;
          }
          
          .prizeCategoryDescription {
            margin-top: 0.9em;
            font-size: 1em;
          }
        }
        
        @media (min-width: 400px) {
          .prizeCategoryImg {
            width: 100px;
            height: 95px;
          }
          
          .prizeCategoryDescription {
            margin-top: 0.9em;
            font-size: 1.1em;
          }
        }
        
        @media (min-width: 900px) {
          .prizeCategoriesSection {
            margin-top: 1em;
            margin-left: -3em;
            width: 400px;
            max-width: unset;
          }
        
          .prizeCategory {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          
          .prizeCategoryImg {
            width: 105px;
            height: 100px;
          }
          
          .prizeCategory .prizeCategoryDescription {
            font-size: 1.2em;
            text-align: center;
            max-width: 200px;
            margin-top: 0.5em;
          }
        }
        
        @media (min-width: 1200px) {
          .prizeCategory .prizeCategoryDescription {
            font-size: 1.4em;
          }
        }
        
        @media (min-width: 1400px) {
          .prizeCategoriesSection {
            margin-top: 1em;
            margin-left: -2em;
            grid-gap: 3em;
            row-gap: 2em;
            width: 400px;
          }
          
          .prizeCategoryImg {
            width: 120px;
            height: 115px;
          }
          
          .prizeCategory .prizeCategoryDescription {
            font-size: 1.5em;
          }
        }
        
        @media (min-width: 1700px) {
          .prizeCategoriesSection {
            width: 450px;
          }
          
          .prizeCategoryImg {
            width: 130px;
            height: 125px;
          }
          
          .prizeCategory .prizeCategoryDescription {
            font-size: 1.7em;
          }
        }
      `}
    </style>
  </div>
);

const getMobileBgImgForVariant = (variant: keyof typeof Variant) => {
  switch (variant) {
    case Variant.GENERAL:
      return "general-bg-mobile.svg";

    case Variant.LA_GOMERA:
      return "la-gomera.jpg";

    case Variant.BARBADOS:
    default:
      return "barbados1.jpg";
  }
};

const getDesktopBgImgForVariant = (variant: keyof typeof Variant) => {
  switch (variant) {
    case Variant.GENERAL:
      return "general-bg.svg";

    case Variant.LA_GOMERA:
      return "la-gomera-desktop.jpg";

    case Variant.BARBADOS:
    default:
      return "barbados1.jpg";
  }
};

const mobileViewStyles = css`
  .headline {
    text-align: center;
    color: white;
    font-size: 1.4em;
    font-weight: 800;
    text-shadow: 2px 2px #4a4a4a;
    margin-top: 0.5em;
  }

  @media (min-width: 330px) {
    .headline {
      margin-top: 1em;
    }
  }

  @media (min-width: 400px) {
    .headline {
      margin-top: 1.5em;
    }
  }

  @media (min-width: 450px) {
    #signUpText {
      font-size: 1.6em;
    }
  }

  .headline.grey {
    color: #4a4a4a;
    text-shadow: 2px 2px white;
  }
`;

interface IMobileViewContentProps {
  variant: keyof typeof Variant;
  onClick: () => void
}

const MobileViewContent = ({ variant, onClick }: IMobileViewContentProps) => {
  switch (variant) {
    case Variant.GENERAL:
      return (
        <>
          <div className="paddedMaxWidthContainer">
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <img
                className={s.logo}
                src={`${ASSET_PREFIX}/images/play-free-lp/daymade-logo-grey.svg`}
                alt="DAYMADE logo"
              />
              <a
                href="https://uk.trustpilot.com/review/daymade.co.uk"
                rel="noreferrer"
                target="_blank"
                style={{ margin: 0, display: "flex", alignItems: "center" }}
              >
                <img
                  className={s.trustpilotLogo}
                  src={`${ASSET_PREFIX}/images/play-free-lp/trustpilot-logo-grey.svg`}
                  alt="TrustPilot logo"
                />
              </a>
            </div>
          </div>
          <div
            id="signUpText"
            className="paddedMaxWidthContainer headline grey"
            style={{ maxWidth: "375px" }}
          >
            Sign up for your FREE weekly chance to win a prize worth £2,000!
          </div>
          <PrizeCategoriesSection onClick={onClick} />
          <style jsx>{mobileViewStyles}</style>
        </>
      );

    case Variant.LA_GOMERA:
      return (
        <>
          <img
            className={s.logo}
            style={{ marginTop: "2em" }}
            src={`${ASSET_PREFIX}/images/play-free-lp/daymade-logo-white.svg`}
            alt="DAYMADE logo"
          />
          <div className="paddedMaxWidthContainer headline">
            Sign up for your FREE weekly chance to win a holiday for 2 to La
            Gomera worth £2000!
          </div>
          <a
            href="https://uk.trustpilot.com/review/daymade.co.uk"
            rel="noreferrer"
            target="_blank"
            style={{
              margin: 0,
              display: "flex",
              alignItems: "center",
              marginRight: "2em",
            }}
          >
            <img
              className={s.trustpilotLogo}
              src={`${ASSET_PREFIX}/images/play-free-lp/trustpilot-logo-white.svg`}
              alt="TrustPilot logo"
            />
          </a>
          <style jsx>{mobileViewStyles}</style>
        </>
      );

    case Variant.BARBADOS:
    default:
      return (
        <>
          <img
            className={s.logo}
            style={{ marginTop: "2em" }}
            src={`${ASSET_PREFIX}/images/play-free-lp/daymade-logo-white.svg`}
            alt="DAYMADE logo"
          />
          <div className="paddedMaxWidthContainer headline">
            Sign up for your FREE weekly chance to win a holiday for 2 to
            Barbados worth £2000!
          </div>
          <a
            href="https://uk.trustpilot.com/review/daymade.co.uk"
            rel="noreferrer"
            target="_blank"
            style={{
              margin: 0,
              display: "flex",
              alignItems: "center",
              marginRight: "2em",
            }}
          >
            <img
              className={s.trustpilotLogo}
              src={`${ASSET_PREFIX}/images/play-free-lp/trustpilot-logo-white.svg`}
              alt="TrustPilot logo"
            />
          </a>
          <style jsx>{mobileViewStyles}</style>
        </>
      );
  }
};

interface IPlayFreeViewProps { 
  variant: keyof typeof Variant
}

const MobileView = ({ variant }: IPlayFreeViewProps) => (
  <div className="container">
    <PlayFreeImageCarousel
      content={
        <MobileViewContent
          variant={variant}
          onClick={() => scrollToSection("#scrollToHere")}
        />
      }
      backgroundImageUrl={`${ASSET_PREFIX}/images/play-free-lp/${getMobileBgImgForVariant(
        variant
      )}`}
      darkenBackground={variant !== Variant.GENERAL}
    />

    <div id="scrollToHere" />

    <PlayFreeSignUpAndEnter />

    {variant !== Variant.GENERAL && (
      <PlayFreeMoreInfoLink
        title="What's included?"
        expandedContent={
          <PaddedMaxWidthContainer>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/dates-grey.png`}
                  className="icon"
                />
                <p className="expandedText iconText">Your choice of dates</p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/flights-grey.png`}
                  className="icon"
                />
                <p className="expandedText iconText">Return flights for 2</p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/hotel-grey.png`}
                  className="icon"
                />
                <p className="expandedText iconText">
                  {variant === Variant.LA_GOMERA ? "7" : "5"} night hotel stay
                </p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/activities-grey.png`}
                  className="icon"
                />
                <p className="expandedText iconText">EPIC activities</p>
              </div>
            </div>
          </PaddedMaxWidthContainer>
        }
      />
    )}

    <PlayFreeMoreInfoLink
      title="About Us"
      expandedContent={
        <PaddedMaxWidthContainer>
          <p className="expandedText">
            DAYMADE is a prize platform where players can win incredible
            holidays, local experiences, home and beauty products, and even your
            favourite tech. Sign-up now for your free weekly chance to win this
            amazing travel prize to Barbados (and many more prizes!) worth
            £2,000. DAYMADE is rated ‘Excellent’ on TrustPilot. Join our
            community of 20,000 winners and counting.
          </p>
        </PaddedMaxWidthContainer>
      }
    />

    <PlayFreeMoreInfoLink
      title="Terms & Conditions"
      expandedContent={
        <PaddedMaxWidthContainer>
          <p className="expandedText">
            By signing up and entering this competition, you agree to our{" "}
            <Link href="/terms-and-conditions">
              <a style={{ textDecoration: "underline", fontWeight: "600" }}>
                Terms & Conditions
              </a>
            </Link>
            .
          </p>
          <p className="expandedText">
            You also agree for DAYMADE to occasionally email you about special
            offers.
          </p>
        </PaddedMaxWidthContainer>
      }
    />

    <BrandDetailsMobileFooter
      backgroundImageUrl={`${ASSET_PREFIX}/images/play-free-lp/barbados1.jpg`}
    />

    <style jsx>
      {`
        .container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .heading {
          margin: 0.5em 0;
          text-transform: uppercase;
          font-weight: 700;
          font-size: 1.1em;
        }

        .expandedText {
          text-align: center;
          margin: 0.5em;
        }

        .iconContainer {
          max-width: 100px;
          text-align: center;
          margin: 0 1em;
        }

        .icon {
          height: 45px;
        }

        .iconText {
          margin: 1em 0 0;
          font-size: 0.9em;
        }

        @media (min-width: 900px) {
          .container {
            display: none;
          }
        }
      `}
    </style>
  </div>
);

const desktopViewStyles = css`
  @media (min-width: 900px) {
    .headline {
      text-align: left;
      font-size: 1.6em;
      color: white;
      font-weight: 800;
      text-shadow: 2px 2px #4a4a4a;
    }

    .headline.grey {
      color: #4a4a4a;
      text-shadow: 2px 2px white;
    }

    #signUpTextDesktop {
      max-width: 550px;
      margin-top: 0;
    }
  }

  @media (min-width: 1200px) {
    .headline {
      font-size: 2em;
    }
  }

  @media (min-width: 1400px) {
    .headline {
      font-size: 2.5em;
    }

    #signUpTextDesktop {
      max-width: 650px;
    }
  }

  @media (min-width: 1700px) {
    .headline {
      font-size: 2.8em;
    }

    #signUpTextDesktop {
      max-width: 750px;
    }
  }

  .includesSection {
    margin: 2em 0;
  }

  .smallHeadline {
    font-size: 2em;
  }

  .iconContainer {
    max-width: 80px;
    text-align: center;
    margin-right: 2em;
  }

  .icon {
    height: 50px;
  }

  .iconText {
    margin-top: 1em;
    font-size: 1.1em;
    font-weight: 600;
    text-align: center;
  }

  @media (min-width: 1400px) {
    .smallHeadline {
      font-size: 2.2em;
    }

    .iconContainer {
      max-width: 90px;
    }

    .icon {
      height: 55px;
    }

    .iconText {
      font-size: 1.2em;
    }
  }

  @media (min-width: 1700px) {
    .smallHeadline {
      font-size: 2.4em;
    }

    .iconContainer {
      max-width: 100px;
    }

    .icon {
      height: 60px;
    }

    .iconText {
      font-size: 1.3em;
    }
  }
`;

const DesktopViewContent = ({ variant }: IPlayFreeViewProps) => {
  switch (variant) {
    case Variant.GENERAL:
      return (
        <>
          <div
            id="signUpTextDesktop"
            className="paddedMaxWidthContainer headline grey"
          >
            Sign up for your FREE weekly chance to win a prize worth £2000!
          </div>
          <PrizeCategoriesSection />
          <style jsx>{desktopViewStyles}</style>
        </>
      );

    case Variant.LA_GOMERA:
      return (
        <>
          <div className="paddedMaxWidthContainer headline">
            Sign up for your FREE weekly chance to win a holiday for 2 to La
            Gomera worth £2000!
          </div>
          <div className="includesSection">
            <p
              className="headline smallHeadline"
              style={{ color: "white", fontWeight: 700, fontStyle: "italic" }}
            >
              Includes:
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                maxWidth: "800px",
                paddingRight: "2em",
              }}
            >
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/dates-white.png`}
                  className="icon"
                  alt="calendar"
                />
                <p className="headline iconText">Your choice of dates</p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/flights-white.png`}
                  className="icon"
                  alt="plane"
                />
                <p className="headline iconText">Return flights for 2</p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/hotel-white.png`}
                  className="icon"
                  alt="house"
                />
                <p className="headline iconText">7 night hotel stay</p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/activities-white.png`}
                  className="icon"
                  alt="compass"
                />
                <p className="headline iconText">EPIC activities</p>
              </div>
            </div>
          </div>
          <style jsx>{desktopViewStyles}</style>
        </>
      );

    case Variant.BARBADOS:
    default:
      return (
        <>
          <div className="paddedMaxWidthContainer headline">
            Sign up for your FREE weekly chance to win a holiday for 2 to
            Barbados worth £2000!
          </div>
          <div className="includesSection">
            <p
              className="headline smallHeadline"
              style={{ color: "white", fontWeight: 700, fontStyle: "italic" }}
            >
              Includes:
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                maxWidth: "800px",
                paddingRight: "2em",
              }}
            >
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/dates-white.png`}
                  className="icon"
                  alt="calendar"
                />
                <p className="headline iconText">Your choice of dates</p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/flights-white.png`}
                  className="icon"
                  alt="plane"
                />
                <p className="headline iconText">Return flights for 2</p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/hotel-white.png`}
                  className="icon"
                  alt="house"
                />
                <p className="headline iconText">5 night hotel stay</p>
              </div>
              <div className="iconContainer">
                <img
                  src={`${ASSET_PREFIX}/images/play-free-lp/activities-white.png`}
                  className="icon"
                  alt="compass"
                />
                <p className="headline iconText">EPIC activities</p>
              </div>
            </div>
          </div>
          <style jsx>{desktopViewStyles}</style>
        </>
      );
  }
};

const DesktopView = ({ variant }: IPlayFreeViewProps) => {
  const [showAboutUsPopup, setShowAboutUsPopup] = useState(false);

  return (
    <div className="container">
      <div className="headerContainer">
        <div className="header">
          <img
            className="logo"
            src={`${ASSET_PREFIX}/images/play-free-lp/daymade-logo-${
              variant === Variant.GENERAL ? "grey" : "white-desktop"
            }.svg`}
            alt="DAYMADE logo"
          />
          <h3
            style={{ letterSpacing: "0", cursor: "pointer", margin: "0" }}
            onClick={() => setShowAboutUsPopup(true)}
          >
            About us
          </h3>
        </div>
      </div>

      {showAboutUsPopup && (
        <AboutUsPopup onClose={() => setShowAboutUsPopup(false)} />
      )}

      <div className="mainContainer">
        <div style={{ height: "100%", width: "75%" }}>
          <PlayFreeImageCarousel
            desktopView
            content={<DesktopViewContent variant={variant} />}
            backgroundImageUrl={`${ASSET_PREFIX}/images/play-free-lp/${getDesktopBgImgForVariant(
              variant
            )}`}
            darkenBackground={variant !== Variant.GENERAL}
            footerStyle={
              variant === Variant.GENERAL
                ? { color: colors.primaryBrandGray }
                : undefined
            }
          />
        </div>

        <div style={{ height: "100%", width: "45%", marginLeft: "-20%" }}>
          <PlayFreeSignUpAndEnter desktopView />
        </div>
      </div>

      <style jsx>
        {`
          .container {
            display: block;
            position: relative;
            width: auto;
            height: auto;
          }

          .headerContainer {
            position: absolute;
            width: 100%;
            z-index: 1;
            padding: 2em 5em;
          }

          .header {
            margin: 0 auto;
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .logo {
            margin-top: 0.1em;
          }

          @media (min-height: 750px) {
            .header {
              padding-top: 1em;
              align-items: center;
            }

            .logo {
              margin-top: 0;
            }
          }

          @media (min-height: 800px) {
            .headerContainer {
              padding: 2em 5em;
            }
          }

          @media (min-height: 900px) {
            .header {
              padding-top: 3em;
            }
          }

          .mainContainer {
            display: flex;
            flex-direction: row;
            align-items: center;
            height: 100vh;
          }

          .imageCarousel {
            width: 80%;
          }

          .logo {
            width: 200px;
          }
        `}
      </style>
    </div>
  );
};

const PlayFreeLP = () => {
  const router = useRouter();
  const variant = router.query?.v as keyof typeof Variant;

  return (
    <MainLayout>
      <NextSeo
        title="DAYMADE | Win Epic Prizes"
        description="Win Epic Prizes. From beauty boxes to brunch in Bali for only £2.95"
        openGraph={{
          title: 'DAYMADE | Win Epic Prizes',
          description:
            'Win Epic Prizes. From beauty boxes to brunch in Bali for only £2.95',
        }}
      />

      <div id="mobile">
        <MobileView variant={variant} />
      </div>
      <div id="desktop">
        <DesktopView variant={variant} />
      </div>

      <style>
        {`
          #mobile {
            display: block;
          }
          
          #desktop {
            display: none;
          }
          
          @media (min-width: 900px) {
            #mobile {
              display: none;
            }
            
            #desktop {
              display: block;
              width: 100%;
              height: 100%;
            }
          }
        `}
      </style>
    </MainLayout>
  );
};

export default PlayFreeLP;
